import React, { useCallback, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage, useT } from "../../lib/LanguageContext";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import usePagedList from "../../hooks/usePagedList";
import LoadFailure from "../../components/ui/LoadFailure";
import Pager from "../../components/ui/Pager";
import StarRating from "../../reviews/components/StarRating";
import { REVIEW_PATHS } from "../../reviews/contract";
import { reviewErrorText } from "../../reviews/errorText";
import { EMPTY, fmtAvg } from "../../reviews/format";
import { REVIEWS } from "../../constants/testIds/reviews";
import ReviewRow from "./ReviewRow";
import FlagDialog from "./FlagDialog";

/**
 * The practitioner's review console.
 *
 * WHAT THE PRACTICE CAN DO, AND WHAT IT CANNOT. Reviews publish immediately and the
 * practice may answer ONCE per review (immutable — a reply that can be edited after the
 * fact is not an answer, it is a second draft of the record) and RAISE A FLAG once. It may
 * not hide anything: moderation is the platform operator's, so the ranking cannot be
 * curated by the practice whose ranking it is. A review the operator has hidden stays
 * visible HERE, marked, because a practitioner is entitled to know what was said about them
 * and what became of it.
 *
 * TWO READS, TWO FAILURE STATES. The summary strip and the paginated list load
 * independently: a failed summary must not blank the rows, and a failed list must not
 * withdraw a number the server did answer.
 */
export default function ReviewsAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [flagTarget, setFlagTarget] = useState(null);

  const list = usePagedList(REVIEW_PATHS.tenantReviews, flaggedOnly ? { flagged: 1 } : {});

  return (
    <div data-testid={REVIEWS.adminPage} className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-4xl text-eh-ink flex items-center gap-3">
          <Star className="w-6 h-6 text-sol-gold" /> {t("rev.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">{t("rev.subtitle")}</p>
      </div>

      <SummaryStrip t={t} tv={tv} />

      <section className="bg-white rounded-2xl border border-eh-border-soft overflow-hidden">
        <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-eh-border-soft">
          <h2 className="font-display text-lg text-eh-ink">{t("rev.list.title")}</h2>
          <label className="flex items-center gap-2 text-sm text-eh-muted">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
              data-testid={REVIEWS.adminFilterFlagged}
            />
            {t("rev.list.filterFlagged")}
          </label>
        </div>

        {list.loadError ? (
          <div className="p-5">
            <LoadFailure
              message={reviewErrorText(list.loadError, { t, tv, fallback: tv("errors.loadFailed") })}
              onRetry={list.reload}
              retryLabel={tv("actions.retry")}
              testId={REVIEWS.adminLoadError}
              retryTestId="reviews-admin-retry"
            />
          </div>
        ) : list.loaded && list.rows.length === 0 ? (
          // ONLY once the read has actually landed: `loaded` is false while the first
          // request is in flight, so "no reviews yet" can never be said about a list nobody
          // has answered yet (BUG-051).
          <div className="p-8 text-center text-eh-muted text-sm" data-testid={REVIEWS.adminEmpty}>
            {t("rev.list.empty")}
          </div>
        ) : (
          <>
            <ul className="m-0 p-0 divide-y divide-eh-border-soft">
              {list.rows.map((r) => (
                <ReviewRow
                  key={r.id}
                  review={r}
                  t={t}
                  tv={tv}
                  lang={lang}
                  onChanged={list.reload}
                  onFlag={() => setFlagTarget(r)}
                />
              ))}
            </ul>
            <Pager
              page={list.page}
              pageSize={list.pageSize}
              total={list.total}
              shown={list.rows.length}
              onPage={list.setPage}
              infoText={(vars) => t("rev.page.info", vars)}
              prevLabel={t("rev.page.prev")}
              nextLabel={t("rev.page.next")}
              testId="reviews-admin-page"
            />
          </>
        )}
      </section>

      {/* ONE dialog for the whole page, not one per row: the contract's flag ids are not
          parameterised, which is the same statement — only one flag can be being written at
          a time. */}
      <FlagDialog
        review={flagTarget}
        t={t}
        tv={tv}
        onClose={(didFlag) => {
          setFlagTarget(null);
          if (didFlag) list.reload();
        }}
      />
    </div>
  );
}

/** The practice average and the per-service breakdown — its own read, its own failure. */
function SummaryStrip({ t, tv }) {
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(REVIEW_PATHS.tenantSummary);
    if (!data || typeof data !== "object" || !data.practice || !Array.isArray(data.services)) {
      throw payloadShapeError();
    }
    setSummary(data);
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  if (loadError) {
    return (
      <LoadFailure
        message={reviewErrorText(loadError, { t, tv, fallback: tv("errors.loadFailed") })}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="reviews-admin-summary-load-error"
        retryTestId="reviews-admin-summary-retry"
      />
    );
  }
  if (!summary) return <p className="text-sm text-eh-muted">{t("rev.loading")}</p>;

  const practice = summary.practice || {};
  const count = Number(practice.review_count) || 0;

  return (
    <section
      data-testid={REVIEWS.adminSummary}
      className="bg-white rounded-2xl border border-eh-border-soft p-5"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <span className="font-display text-4xl tabular-nums text-eh-ink">{fmtAvg(practice.avg_rating)}</span>
        <StarRating
          value={practice.avg_rating ?? 0}
          readOnly
          variant="light"
          size="md"
          label={count ? t("rev.summary.aria", { avg: fmtAvg(practice.avg_rating) }) : t("rev.summary.none")}
        />
        <span className="text-sm text-eh-muted">
          {count ? t("rev.summary.count", { count }) : t("rev.summary.none")}
        </span>
      </div>

      {summary.services.length > 0 && (
        <ul className="m-0 p-0 mt-4 space-y-1.5">
          {summary.services.map((s) => (
            <li
              key={s.service_id || s.service_name}
              className="list-none flex items-center justify-between gap-4 text-sm"
              data-testid={`reviews-admin-summary-service-${s.service_id || "none"}`}
            >
              <span className="min-w-0 truncate text-eh-ink">{s.service_name || EMPTY}</span>
              <span className="tabular-nums text-eh-muted shrink-0">
                {fmtAvg(s.rating_summary?.avg_rating)}
                {" · "}
                {t("rev.summary.count", { count: Number(s.rating_summary?.review_count) || 0 })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
